// @ts-nocheck
// useStatesandActions.js
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useUser } from 'hooks';
import { LOCAL_STORAGE_KEYS } from 'constant';
import { useLocation } from 'react-router-dom';
import { initialState, actionTypes, reducer } from './useReducer';
import useApiServices from './useApiandServices';

const useStatesandActions = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isDirty, setIsDirty] = useState(false);
  const { serverState, apiActions } = useApiServices();

  const [currentStatus, setCurrentStatus] = useState('published');
  const [isCreateStatus, setIsCreateStatus] = useState(false);
  const [currentFormListTable, setCurrentFormListTable] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [searchTags, setSearchTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [notifiedUsers, setNotifiedUsers] = useState([]);
  const [customEmails, setCustomEmails] = useState([]);
  const [actionStatus, setActionStatus] = useState('Need Action');
  const [actionStatusColor, setActionStatusColor] = useState('#F44336');
  const responseRef = useRef(false);
  const [mode, setMode] = useState('');
  const [isResponseDirty, setIsResponseDirty] = useState(false);
  const initialRef = useRef(false);

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: localStorage.getItem(LOCAL_STORAGE_KEYS.PER_PAGE) ?? 15,
  });

  const [sorting, setSorting] = useState<{ id: string; desc: boolean }[]>([]);

  const { user } = useUser();
  const isFormManagePermissionAllowed =
    user?.data?.customform?.can_manage_customform;

  const location = useLocation();

  const getCurrentStatus = () => {
    let status;
    if (location.pathname.includes('form-template')) {
      status = 'draft';
    } else if (currentStatus === 'draft') {
      status = 'draft';
    } else {
      status = 'published';
    }

    return { status };
  };

  const getTemplatesQueryParams = () => {
    const params: { [key: string]: string } = {};

    if (location.pathname.includes('form-template')) {
      const formattedTags = searchTags?.length
        ? searchTags?.map((tag) => tag.title)
        : undefined;

      params.tags = formattedTags;

      sorting.forEach((sort) => {
        let field;
        switch (sort.id) {
          case 'updatedAt':
            field = 'updated_at';
            break;
          case 'title':
            field = 'form_title';
            break;
          default:
            field = sort.id;
        }
        const direction = sort.desc ? 'desc' : 'asc';
        params[`sort_by_${field}`] = direction;
      });
    }
    return params;
  };
  const isTemplate = Boolean(location.pathname.includes('form-template'));

  const getQueryParamsForFetchFormData = () => ({
    per_page: pagination.pageSize,
    page: pagination.pageIndex + 1,
    title: searchText,
    is_template: isTemplate,
    ...getCurrentStatus(),
    ...getTemplatesQueryParams(),
  });

  useEffect(() => {
    apiActions.fetchFormData({
      query: { ...getQueryParamsForFetchFormData() },
    });
  }, [pagination, searchText, searchTags, sorting, isTemplate]);

  const resetPageIndex = () => {
    if (pagination.pageIndex === 0) {
      return;
    }
    setPagination({ ...pagination, pageIndex: 0 });
  };

  const setDirtyAndDispatch = useCallback((action) => {
    setIsDirty(true);
    dispatch(action);
  }, []);

  const setResponseDirtyAndDispatch = useCallback((action) => {
    setIsResponseDirty(true);
    dispatch(action);
  }, []);

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.PER_PAGE, pagination.pageSize);
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.PAGE_NUMBER,
      pagination.pageIndex + 1,
    );
  }, [pagination]);

  const resetFormField = () => {
    dispatch({ type: actionTypes.RESET_FORM });
    setCurrentFormListTable(0);
    setActionStatus(null);
    setSelectedTags([]);
    setNotifiedUsers([]);
    setCustomEmails([]);
    dispatch({
      type: actionTypes.SET_ASSIGN_ALL_PROJECTS,
      payload: false,
    });
    dispatch({
      type: actionTypes.SET_ASSIGN_ALL_USERS,
      payload: false,
    });
    dispatch({
      type: actionTypes.SET_ASSIGN_ALL_EMPLOYEES,
      payload: false,
    });
  };
  const extendedState = {
    currentFormListTable,
    currentStatus,
    isCreateStatus,
    searchTags,
    searchText,
    pagination,
    sorting,
    actionStatus,
    actionStatusColor,
    isDirty,
    mode,
    isResponseDirty,
    isFormManagePermissionAllowed,
    selectedTags,
    notifiedUsers,
    customEmails,
    ...state,
    ...serverState,
  };
  const extendedAction = {
    initialRef,
    responseRef,
    setCurrentFormListTable,
    setSearchText,
    setPagination,
    setCurrentStatus,
    setIsCreateStatus,
    setIsDirty,
    resetFormField,
    setActionStatus,
    setActionStatusColor,
    setMode,
    setIsResponseDirty,
    setSelectedTags,
    setNotifiedUsers,
    setCustomEmails,
    setSearchTags,
    setSorting,
    resetPageIndex,
  };

  const actions = {
    setItems: (items) =>
      dispatch({ type: actionTypes.SET_ITEMS, payload: items }),
    setTitle: (title) => {
      dispatch({ type: actionTypes.SET_TITLE, payload: title });
    },
    setDescription: (description) => {
      dispatch({ type: actionTypes.SET_DESCRIPTION, payload: description });
    },
    setFormFields: (formFields) =>
      dispatch({ type: actionTypes.SET_FORM_FIELDS, payload: formFields }),
    setFormJson: (formJson) =>
      dispatch({ type: actionTypes.SET_FORM_JSON, payload: formJson }),
    setIsRequired: (fieldId, isRequired) =>
      dispatch({
        type: actionTypes.SET_IS_REQUIRED,
        payload: { id: fieldId, isRequired },
      }),
    setIsConditional: (fieldId, isConditional) =>
      dispatch({
        type: actionTypes.SET_IS_CONDITIONAL,
        payload: { id: fieldId, isConditional },
      }),

    setVersion: (version) =>
      dispatch({ type: actionTypes.SET_VERSION, payload: version }),

    setLabel: (fieldId, label) => {
      dispatch({
        type: actionTypes.SET_LABEL,
        payload: { id: fieldId, label },
      });
    },
    setInstruction: (fieldId, instruction) => {
      dispatch({
        type: actionTypes.SET_INSTRUCTION,
        payload: { id: fieldId, instruction },
      });
    },
    setSection: (fieldId, section) =>
      dispatch({
        type: actionTypes.SET_SECTION,
        payload: { id: fieldId, section },
      }),
    setAttachments: (fieldId, attachments) =>
      dispatch({
        type: actionTypes.SET_ATTACHMENTS,
        payload: { id: fieldId, attachments },
      }),
    setAnswer: (fieldId, answer) =>
      dispatch({
        type: actionTypes.SET_ANSWER,
        payload: { id: fieldId, answer },
      }),
    setSelectedLinkListLabel: (fieldId, selectedLinkListLabel) =>
      dispatch({
        type: actionTypes.SET_SELECTED_LINK_LIST_LABEL,
        payload: { id: fieldId, selectedLinkListLabel },
      }),

    setEmployeeRole: (employeeRole) =>
      dispatch({ type: actionTypes.SET_EMPLOYEE_ROLE, payload: employeeRole }),
    setEmployeeData: (employeeData) =>
      dispatch({ type: actionTypes.SET_EMPLOYEE_DATA, payload: employeeData }),
    setUserRole: (userRole) =>
      dispatch({ type: actionTypes.SET_USER_ROLE, payload: userRole }),
    setUserData: (userData) =>
      dispatch({ type: actionTypes.SET_USER_DATA, payload: userData }),
    setEquipmentType: (equipmentType) =>
      dispatch({
        type: actionTypes.SET_EQUIPMENT_TYPE,
        payload: equipmentType,
      }),
    setEquipmentData: (equipmentData) =>
      dispatch({
        type: actionTypes.SET_EQUIPMENT_DATA,
        payload: equipmentData,
      }),
    setProjectData: (projectData) =>
      dispatch({
        type: actionTypes.SET_PROJECT_DATA,
        payload: projectData,
      }),
    setCurrentLocation: (currentLocation) =>
      dispatch({
        type: actionTypes.SET_CURRENT_LOCATION,
        payload: currentLocation,
      }),
    setProjectTimesheetRequired: (isProjectTimesheetRequired) =>
      dispatch({
        type: actionTypes.SET_PROJECT_TIMESHEET_REQUIRED,
        payload: isProjectTimesheetRequired,
      }),
    setProjectClockInCompulsory: (isProjectClockInCompulsory) =>
      dispatch({
        type: actionTypes.SET_PROJECT_CLOCK_IN_COMPULSORY,
        payload: isProjectClockInCompulsory,
      }),
    setEmployeeTimesheetRequired: (isEmployeeTimesheetRequired) =>
      dispatch({
        type: actionTypes.SET_EMPLOYEE_TIMESHEET_REQUIRED,
        payload: isEmployeeTimesheetRequired,
      }),
    setEmployeeClockInCompulsory: (isEmployeeClockInCompulsory) =>
      dispatch({
        type: actionTypes.SET_EMPLOYEE_CLOCK_IN_COMPULSORY,
        payload: isEmployeeClockInCompulsory,
      }),
    setEquipmentOneResponsePerDay: (isEquipmentOneResponsePerDay) =>
      dispatch({
        type: actionTypes.SET_EQUIPMENT_ONE_RESPONSE_PER_DAY,
        payload: isEquipmentOneResponsePerDay,
      }),
    setEquipmentShowPastResponses: (isEquipmentShowPastResponses) =>
      dispatch({
        type: actionTypes.SET_EQUIPMENT_SHOW_PAST_RESPONSES,
        payload: isEquipmentShowPastResponses,
      }),
    setEquipmentClockInCompulsory: (isEquipmentClockInCompulsory) =>
      dispatch({
        type: actionTypes.SET_EQUIPMENT_CLOCK_IN_COMPULSORY,
        payload: isEquipmentClockInCompulsory,
      }),
    setIsStatusOnSubmission: (isStatusOnSubmission) =>
      dispatch({
        type: actionTypes.SET_IS_STATUS_ON_SUBMISSION,
        payload: isStatusOnSubmission,
      }),
    setIsCollectGeolocation: (isCollectGeolocation) =>
      dispatch({
        type: actionTypes.SET_IS_COLLECT_GEOLOCATION,
        payload: isCollectGeolocation,
      }),
    setIsEditResponse: (isEditResponse) =>
      dispatch({
        type: actionTypes.SET_IS_EDIT_RESPONSE,
        payload: isEditResponse,
      }),

    updateTable: (fieldId, key, value) =>
      setDirtyAndDispatch({
        type: actionTypes.UPDATE_TABLE,
        payload: { fieldId, key, value },
      }),

    addColumn: (fieldId) =>
      setDirtyAndDispatch({
        type: actionTypes.ADD_COLUMN,
        payload: { fieldId },
      }),

    addColumnFromDrawer: (fieldId) =>
      setDirtyAndDispatch({
        type: actionTypes.ADD_COLUMN_FROM_DRAWER,
        payload: {},
      }),

    addRow: (fieldId) =>
      setDirtyAndDispatch({
        type: actionTypes.ADD_ROW,
        payload: { fieldId },
      }),
    deleteRow: (fieldId, rowIndex) =>
      setDirtyAndDispatch({
        type: actionTypes.DELETE_ROW,
        payload: { fieldId, rowIndex },
      }),
    deleteColumn: (fieldId, columnIndex) =>
      setDirtyAndDispatch({
        type: actionTypes.DELETE_COLUMN,
        payload: { fieldId, columnIndex },
      }),

    duplicateColumn: (fieldId, columnIndex) =>
      setDirtyAndDispatch({
        type: actionTypes.DUPLICATE_COLUMN,
        payload: { fieldId, columnIndex },
      }),

    duplicateRow: (fieldId, rowIndex) =>
      setDirtyAndDispatch({
        type: actionTypes.DUPLICATE_ROW,
        payload: { fieldId, rowIndex },
      }),

    editTableRow: (fieldIndex, contentIndex) =>
      setDirtyAndDispatch({
        type: actionTypes.EDIT_TABLE_ROW,
        payload: { fieldIndex, contentIndex },
      }),

    editTableColumn: (fieldIndex, contentIndex) =>
      setDirtyAndDispatch({
        type: actionTypes.EDIT_TABLE_COLUMN,
        payload: { fieldIndex, contentIndex },
      }),

    editTableHeaders: (fieldIndex) =>
      setDirtyAndDispatch({
        type: actionTypes.EDIT_TABLE_HEADERS,
        payload: { fieldIndex },
      }),
    updateTableRow: () =>
      setDirtyAndDispatch({
        type: actionTypes.UPDATE_TABLE_ROW,
        payload: {},
      }),

    updateTableColumn: () =>
      setDirtyAndDispatch({
        type: actionTypes.UPDATE_TABLE_COLUMN,
        payload: {},
      }),

    updateTableHeaders: () =>
      setDirtyAndDispatch({
        type: actionTypes.UPDATE_TABLE_HEADERS,
        payload: {},
      }),

    updateColumnHeader: (headerIndex, header) =>
      setDirtyAndDispatch({
        type: actionTypes.UPDATE_COLUMN_HEADER,
        payload: { headerIndex, header },
      }),

    updateColumnType: (contentIndex, fieldType) =>
      setDirtyAndDispatch({
        type: actionTypes.UPDATE_COLUMN_TYPE,
        payload: { contentIndex, fieldType },
      }),

    updateColumn: (contentIndex, properties) =>
      setDirtyAndDispatch({
        type: actionTypes.UPDATE_COLUMN,
        payload: { contentIndex, properties },
      }),

    updateColumnRequiredField: (contentIndex, isRequired) =>
      dispatch({
        type: actionTypes.UPDATE_COLUMN_REQUIRED_FIELD,
        payload: { contentIndex, isRequired },
      }),

    updateChoice: (contentIndex, choiceIndex, key, value) =>
      setDirtyAndDispatch({
        type: actionTypes.UPDATE_CHOICE,
        payload: { contentIndex, choiceIndex, key, value },
      }),
    reorderTableRow: (fieldIndex, rowSourceIndex, rowDestinationIndex) =>
      setDirtyAndDispatch({
        type: actionTypes.REORDER_TABLE_ROW,
        payload: { fieldIndex, rowSourceIndex, rowDestinationIndex },
      }),

    resizeTableColumn: (fieldIndex, columnIndex, columnWidth) =>
      setDirtyAndDispatch({
        type: actionTypes.RESIZE_TABLE_COLUMN,
        payload: { fieldIndex, columnIndex, columnWidth },
      }),

    updateAnswer: (fieldId, rowIndex, colIndex, answer) =>
      setResponseDirtyAndDispatch({
        type: actionTypes.UPDATE_ANSWER,
        payload: { fieldId, rowIndex, colIndex, answer },
      }),
    updatePreviewColumn: (fieldId, rowIndex, colIndex, properties) =>
      setResponseDirtyAndDispatch({
        type: actionTypes.UPDATE_PREVIEW_COLUMN,
        payload: { fieldId, rowIndex, colIndex, properties },
      }),
    setTableIsRequired: (fieldId, isRequired) =>
      dispatch({
        type: actionTypes.SET_TABLE_IS_REQUIRED,
        payload: { fieldId, isRequired },
      }),
    setAssignAllProjects: (assignAllProjects) =>
      dispatch({
        type: actionTypes.SET_ASSIGN_ALL_PROJECTS,
        payload: assignAllProjects,
      }),
    setAssignAllUsers: (assignAllUsers) =>
      dispatch({
        type: actionTypes.SET_ASSIGN_ALL_USERS,
        payload: assignAllUsers,
      }),
    setAssignAllEmployees: (assignAllEmployees) =>
      dispatch({
        type: actionTypes.SET_ASSIGN_ALL_EMPLOYEES,
        payload: assignAllEmployees,
      }),
    setAIDescription: (aiDescription) =>
      dispatch({
        type: actionTypes.SET_AI_DESCRIPTION,
        payload: aiDescription,
      }),
    setSubmissionFrequency: (submissionFrequency) =>
      dispatch({
        type: actionTypes.SET_SUBMISSION_FREQUENCY,
        payload: submissionFrequency?.label,
      }),
    setWeekStart: (weekStart) =>
      dispatch({
        type: actionTypes.SET_WEEK_START,
        payload: weekStart?.label,
      }),

    // Api actions
    ...apiActions,
    ...extendedAction,
  };

  return [extendedState, actions];
};

export default useStatesandActions;
